import { Instance, SnapshotIn, SnapshotOut, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import { ICreateProduct, deleteProduct, getProduct, updateProduct } from '@common/api/ecommerce';
import handleApiErrors from '@common/helpers/handleApiErrors';

import AttributeModel from '../Attributes/Attribute';
import CgCounselorModel from '../Users/CgCounselors/CgCounselor';
import { CgPartnerSummaryModel } from '../Users/CgPartners/CgPartner';
import { IProductsModelInstance } from './Products';

export const Category = types.model('Category', {
	id: types.identifierNumber,
	name: types.string,
	is_read_only: types.optional(types.boolean, false),
	deleted_at: types.maybe(types.maybeNull(types.string)),
	created_at: types.maybe(types.maybeNull(types.string)),
	updated_at: types.maybe(types.maybeNull(types.string)),
});

export const CgTariffRelation = types.model('CgTariffRelation', {
	id: types.identifierNumber,
	name: types.string,
	deleted_at: types.maybe(types.maybeNull(types.string)),
	laravel_through_key: types.maybe(types.number),
});

export const CgTariff = types.model('CgTariff', {
	id: types.identifierNumber,
	name: types.string,
	relations: types.optional(types.array(CgTariffRelation), []),
});

export const Product = types
	.model('Product', {
		id: types.identifierNumber,
		name: types.maybeNull(types.string),
		description: types.maybeNull(types.string),
		price: types.maybeNull(types.union(types.string, types.number)),
		price_range: types.maybeNull(types.string),
		currency: types.maybeNull(types.string),
		currency_per: types.maybeNull(types.string),
		category: types.maybeNull(Category),
		cgTariff: types.maybe(types.maybeNull(CgTariff)),
		attributes: types.optional(types.array(AttributeModel), []),
		businesses: types.optional(types.array(types.frozen()), []),
		is_one_time_purchase: types.optional(types.boolean, false),
		is_published: types.optional(types.boolean, false),
		is_purchased: types.optional(types.boolean, false),

		image: types.maybe(types.maybeNull(types.string)),
		title: types.maybe(types.maybeNull(types.string)),
		short_name: types.maybe(types.maybeNull(types.string)),
		block_1: types.maybe(types.maybeNull(types.string)),
		block_2: types.maybe(types.maybeNull(types.string)),
		block_3: types.maybe(types.maybeNull(types.string)),
		block_4: types.maybe(types.maybeNull(types.string)),
		focusArea: types.maybe(types.maybeNull(types.string)),
		c_g_counselors: types.optional(types.array(CgCounselorModel), []),
		c_g_counselor_ids: types.optional(types.array(types.number), []),
		c_g_partners: types.optional(types.array(CgPartnerSummaryModel), []),
		c_g_partner_ids: types.optional(types.array(types.number), []),
		tags: types.optional(types.array(types.string), []),
		c_g_group_consultations: types.optional(types.array(types.frozen()), []),
		c_g_group_consultation_ids: types.optional(types.array(types.number), []),
	})
	.volatile(() => ({
		loading: false,
	}))
	.actions((self) => ({
		fetch: flow(function* (id: number) {
			self.loading = true;
			try {
				const productId = id || self?.id;
				if (!productId) {
					return;
				}
				const result = yield getProduct({ id: productId });
				applySnapshot(self, result);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield deleteProduct({ id: self.id });
				const parent = getParent<IProductsModelInstance>(self, 2);
				parent.removeItem(self);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.loading = false;
			}
		}),
		save: flow(function* (data: ICreateProduct) {
			self.loading = true;
			try {
				const result = yield updateProduct({ id: self.id, payload: data });

				applySnapshot(self, result);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.loading = false;
			}
		}),
	}));

export const StudentProduct = types.model('StudentProduct', {
	id: types.identifierNumber,
	name: types.string,
	description: types.string,
	price: types.string,
	currency: types.string,
	category_id: types.number,
	deleted_at: types.maybeNull(types.string),
	created_at: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
	pivot: types.frozen(),
});

export default Product;

export type ICategoryModelInstance = Instance<typeof Category>;
export type ICategoryModelSnapshotIn = SnapshotIn<typeof Category>;
export type ICategoryModelSnapshotOut = SnapshotOut<typeof Category>;

export type IProductModelInstance = Instance<typeof Product>;
export type IProductModelSnapshotIn = SnapshotIn<typeof Product>;
export type IProductModelSnapshotOut = SnapshotOut<typeof Product>;

export type IStudentProductModelInstance = Instance<typeof StudentProduct>;
export type IStudentProductModelSnapshotIn = SnapshotIn<typeof StudentProduct>;
export type IStudentProductModelSnapshotOut = SnapshotOut<typeof StudentProduct>;

export type ICategoryModel = ICategoryModelInstance;
export type IProductModel = IProductModelInstance;
